import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { StyleSheet, View, FlatList, SafeAreaView, ActivityIndicator } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import usePreviousValue from '../utils/usePreviousValue';
import { useSelector, useDispatch } from 'react-redux';
import { MyTripsCard } from '../components/resultCards';
import BaseText from '../components/BaseText';
import InputForm from '../components/InputForm';
import { leftMenuHeaderOption, rightFilterHeaderOption } from '../utils/defaultNavOptions';
import {
  selectTripsCount,
  selectFilteredTrips,
  selectTripIsAnyFilterApplied,
} from '../store/selectors/mytrips.selector';
import { clearMyTrips, getMyTrips, selectTrip, updateMyTrips } from '../store/actions/mytrips';
import { selectMytripsCount } from '../store/selectors/mytrips.selector';

import { CommonStyles, CommonProperties, Color } from '../styles';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import { Screens, Modals } from '../constants/ScreenNames';
import useTextTripFilter from '../utils/useTextTripFilter';
import MultipleSelect from '../components/SegmentedControl/MultipleSelect';
import { tripsFilter } from '../constants/Enums';
import moment from 'moment';

const MyTripsScreen = (props) => {
  const { t } = useTranslation(i18nNS.myTrips);
  const { tKey } = useComponentTKey('MyTripsScreen');
  const dispatch = useDispatch();
  const [filters] = useTextTripFilter();

  const myTripResponse = useSelector(selectFilteredTrips);
  const isAnyFilterApplied = useSelector(selectTripIsAnyFilterApplied);
  const myTripsCount = useSelector(selectMytripsCount);

  const [searchValue, setSearchValue] = useState('');
  const [tripData, setData] = useState(myTripResponse);
  const [filterStatus, setFilterStatus] = useState('AllTrips');
  const [isFirstRender, setIsFirstRender] = useState(true);

  const [itemsLoading, setItemsLoading] = useState(true);
  const increamentValue = myTripsCount || 0;
  const [range, setCountRange] = useState([1, increamentValue]);
  const previousAppliedFilter = usePreviousValue({ filterStatus });
  const [filterData, setFilterData] = useState(undefined);
  useFocusEffect(
    useCallback(() => {
      setItemsLoading(true);
      if (props?.route?.params?.fromFilterOrTripHubScreen) {
        props.route.params.fromFilterOrTripHubScreen = false;
        setItemsLoading(false);
        return;
      }
      setIsFirstRender(true);
      setFilterStatus('AllTrips');
      setCountRange([1, increamentValue]);
      dispatch(clearMyTrips());
      getFirstList();
    }, [props.route.params])
  );

  const getFirstList = async () => {
    await dispatch(getMyTrips(1, increamentValue, ''));
    setIsFirstRender(false);
  };

  useEffect(() => {
    setData(myTripResponse);
    if (myTripResponse?.length > 0) {
      setItemsLoading(false);
    }
  }, [myTripResponse]);

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerTitle: () => (
        <InputForm
          value={searchValue}
          placeholder={t(tKey('FindTrips'))}
          style={CommonStyles.mb0}
          clearButtonMode="always"
          width={250}
          onChangeText={(text) => {
            setSearchValue(text);
          }}
        />
      ),
      headerTitleAlign: 'left',
      ...rightFilterHeaderOption(() => {
        props.navigation.navigate(Modals.TripFilterModal);
      }, isAnyFilterApplied),
      ...leftMenuHeaderOption(props),
    });
  }, [props, setSearchValue, searchValue, isAnyFilterApplied, t, tKey]);

  useEffect(() => {
    let trips = myTripResponse;

    if (searchValue?.length >= 1) {
      if (filterStatus !== 'AllTrips') {
        trips = filterData?.filter((item) => {
          const itemData = `${item.TripFromCity.toLowerCase()} ${item.TripToCity.toLowerCase()} ${item.Recordlocator.toLowerCase()} ${item.TripName.toLowerCase()}`;
          const textData = searchValue.toLowerCase();
          return itemData.indexOf(textData) > -1;
        });
      } else {
        trips = myTripResponse.filter((item) => {
          const itemData = `${item.TripFromCity.toLowerCase()} ${item.TripToCity.toLowerCase()} ${item.Recordlocator.toLowerCase()} ${item.TripName.toLowerCase()}`;
          const textData = searchValue.toLowerCase();
          return itemData.indexOf(textData) > -1;
        });
      }
      setData(trips);
    } else {
      if (filterStatus === 'AllTrips') {
        setData(myTripResponse);
      } else {
        setData(filterData);
      }
    }
  }, [searchValue, filterData]);

  const filterType = {
    Pending: 1,
    UpcomingPurchase: 2,
    Past: 3,
    Canceled: 5,
    AllTrips: '',
  };

  const applyFilter = () => {
    let filteredData = [];

    switch (filterStatus) {
      case tripsFilter.PENDING:
      case tripsFilter.CANCELED:
      case tripsFilter.APPROVED:
      case tripsFilter.PENDING_CANCEL:
      case tripsFilter.PENDING_APPROVAL:
      case tripsFilter.PENDING_CREDIT_CARD:
        filteredData = myTripResponse?.filter((trip) => trip.TripFilterType === filterStatus);
        break;
      case tripsFilter.UPCOMING_PURCHASE:
        filteredData = myTripResponse?.filter(
          (trip) => trip.TripFilterType === filterStatus && !moment(trip.EndDate).isBefore()
        );
        break;
      case tripsFilter.PAST:
        filteredData = myTripResponse?.filter((trip) => moment(trip.EndDate).isBefore());
        break;
      default:
        filteredData = myTripResponse?.filter(
          (trip) => trip.TripFilterType === previousAppliedFilter?.filterStatus
        );
        break;
    }

    if (filterStatus === 'AllTrips') {
      setData(myTripResponse);
    } else {
      setFilterData(filteredData);
      setData(filteredData);
    }
  };

  const searchByFilter = (isInfinite) => {
    //this condition is added to avoid double API loading at first render
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }
    setItemsLoading(true);
    if (filterStatus) {
      setSearchValue('');
      applyFilter();
    }
    setItemsLoading(false);
  };

  useEffect(() => {
    searchByFilter();
  }, [filterStatus]);

  const loadMoreTrips = async () => {
    if (itemsLoading || range[1] > myTripsCount) {
      return;
    }
    setItemsLoading(true);
    await searchByFilter('isInfinite');
    setItemsLoading(false);
  };

  const checkLoadMoreTrips = () => {
    if (props.route.params == undefined && props?.route?.params?.fromFilterOrTripHubScreen) {
      return;
    }
    if (!searchValue) {
      loadMoreTrips();
    }
  };

  const renderFooter = () => {
    if (!itemsLoading) {
      return null;
    }

    return <ActivityIndicator animating size="small" color={Color.primary} />;
  };

  return (
    <SafeAreaView style={styles.pageContainer}>
      <View style={CommonStyles.flex1}>
        <MultipleSelect
          items={filters}
          onChange={(value) => {
            value && setFilterStatus(value);
          }}
          selected={filterStatus}
        />
        <FlatList
          style={CommonStyles.flex1}
          data={tripData}
          showsVerticalScrollIndicator={false}
          keyExtractor={(item, index) => index}
          onEndReachedThreshold={0.1}
          onEndReached={checkLoadMoreTrips}
          initialNumToRender={increamentValue}
          renderItem={({ item }) => (
            <MyTripsCard
              style={CommonStyles.boxShadow}
              item={item}
              onPress={() => {
                dispatch(selectTrip(item));
                props.navigation.navigate(Screens.TripHub, {
                  tripKey: item.TripKey,
                  showInvoiceFirst: false,
                });
              }}
            />
          )}
          ListEmptyComponent={() =>
            !itemsLoading && <BaseText style={styles.textInfo}>{t(tKey('NoTripsFound'))}</BaseText>
          }
          ListFooterComponent={renderFooter}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  textInfo: {
    textAlign: 'center',
    fontWeight: 'bold',
    ...CommonProperties.mt3,
  },
  loadingIcon: {
    ...CommonProperties.my3,
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default MyTripsScreen;
